import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
  MAX_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
  MIN_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
} from '@klareospace/pharos-lib-types';
import type { Site } from '@klareospace/pharos-lib-types';

import type { UpdateSiteDefaultsFormInput } from '../schemas/site-defaults.schema';
import { useResetFormOnSiteUpdate } from './use-reset-form-on-site-update';
import { useUpdateSite } from './use-update-site';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { UpdateSiteDefaultsFormInput };

export interface UseUpdateSiteDefaultsFormReturn {
  form: UseFormReturn<UpdateSiteDefaultsFormInput>;
  isDirty: boolean;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: UpdateSiteDefaultsFormInput) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDefaultValues(site: Site): UpdateSiteDefaultsFormInput {
  return {
    deliveryTimeslotDurationInHours: String(
      site.deliveryTimeslotDurationInHours ?? DEFAULT_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
    ),
  };
}

function isValidTimeslotDuration(value: string): boolean {
  const parsed = Number(value);
  return (
    Number.isInteger(parsed) &&
    parsed >= MIN_DELIVERY_TIMESLOT_DURATION_IN_HOURS &&
    parsed <= MAX_DELIVERY_TIMESLOT_DURATION_IN_HOURS
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateSiteDefaultsForm(site: Site): UseUpdateSiteDefaultsFormReturn {
  const { t } = useTranslation('settings');
  const updateSite = useUpdateSite(site.id);

  const schema = useMemo(
    () =>
      z.object({
        deliveryTimeslotDurationInHours: z.string().refine(
          isValidTimeslotDuration,
          t('sites.settings.defaults.validation.timeslotDurationInvalid', {
            max: MAX_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
            min: MIN_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
          }),
        ),
      }),
    [t],
  );

  const form = useForm<UpdateSiteDefaultsFormInput>({
    defaultValues: toDefaultValues(site),
    mode: 'all',
    resolver: zodResolver(schema),
  });

  useResetFormOnSiteUpdate(form, site, toDefaultValues);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const onSubmit = useCallback(
    (data: UpdateSiteDefaultsFormInput): void => {
      updateSite.mutateAsync({ deliveryTimeslotDurationInHours: Number(data.deliveryTimeslotDurationInHours) }).then(
        () => {
          toast.success(t('sites.settings.updateSuccess'));
        },
        () => {
          toast.error(t('sites.settings.updateError'));
        },
      );
    },
    [t, updateSite],
  );

  const onCancel = useCallback((): void => {
    form.reset();
  }, [form]);

  return {
    form,
    isDirty: form.formState.isDirty,
    isPending: updateSite.isPending,
    onCancel,
    onSubmit,
  };
}
